/**
 * AI Interview API client.
 *
 * The AI Interview endpoints are PUBLIC on the FastAPI backend and are exempt from
 * the app's transport encryption + auth. So — unlike `utils/aiAutomationApi.js` —
 * we talk to them with plain JSON `fetch` (no CryptoJS, no Bearer token).
 *
 * Base URL mirrors aiAutomationApi's resolver (REACT_APP_AI_AUTOMATION_URL, with a
 * same-origin fallback in production).
 */

const AI_AUTOMATION_URL = process.env.REACT_APP_AI_AUTOMATION_URL;
const SOCKET_URL = process.env.REACT_APP_SERVERSOCKET_URL;
const LOCAL_HOSTNAMES = new Set(['localhost', '127.0.0.1', '0.0.0.0', '::1']);

export const resolveBaseUrl = () => {
  const isBrowser = typeof window !== 'undefined' && typeof window.location !== 'undefined';
  const origin = isBrowser ? window.location.origin : '';

  if (AI_AUTOMATION_URL) {
    try {
      const parsed = new URL(AI_AUTOMATION_URL, origin || 'http://localhost');
      const host = isBrowser ? window.location.hostname : '';
      // Running on a real host but the configured API points at localhost -> use same origin.
      if (host && !LOCAL_HOSTNAMES.has(host) && LOCAL_HOSTNAMES.has(parsed.hostname)) {
        return isBrowser ? `${origin}/api/v1` : parsed.toString().replace(/\/+$/, '');
      }
      return parsed.toString().replace(/\/+$/, '');
    } catch {
      return AI_AUTOMATION_URL.replace(/\/+$/, '');
    }
  }
  return isBrowser ? `${origin}/api/v1` : 'http://localhost:8100/api/v1';
};

const BASE = resolveBaseUrl();
let interviewLease = '';
let scheduledInterviewId = '';
let onInterviewLeaseLost = null;
let leaseGeneration = 0;
const managedPresenceSessions = new Set();
const requestOwners = new WeakMap();
const presenceSequences = new Map();
const nextPresenceSequence = (owner) => {
  const sequence = (presenceSequences.get(owner) || 0) + 1;
  presenceSequences.set(owner, sequence);
  return sequence;
};

export const setInterviewLease = (owner, onLost, scheduledId = '') => {
  if (interviewLease !== (owner || '') || scheduledInterviewId !== (scheduledId || '')) leaseGeneration += 1;
  interviewLease = owner || '';
  scheduledInterviewId = scheduledId || '';
  onInterviewLeaseLost = onLost || null;
};

const interviewFetch = (url, options = {}) => {
  const ownership = { owner: interviewLease, generation: leaseGeneration, onLost: onInterviewLeaseLost };
  const headers = { ...(options.headers || {}) };
  if (interviewLease) {
    headers['X-Interview-Lease'] = interviewLease;
    headers['X-Scheduled-Interview-Id'] = scheduledInterviewId;
  }
  return fetch(url, { ...options, headers }).then((response) => {
    requestOwners.set(response, ownership);
    return response;
  });
};

// FastAPI returns 422 validation errors as `detail: [{loc, msg, type}, ...]`. Stringifying that array
// (via `new Error(detail)`) yields the useless "[object Object]". Flatten it to a readable message.
function detailToMessage(detail) {
  if (!detail) return '';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail
      .map((d) => (d && typeof d === 'object' ? (d.msg || JSON.stringify(d)) : String(d)))
      .filter(Boolean)
      .join('; ');
  }
  if (typeof detail === 'object') return detail.msg || detail.message || detail.code || JSON.stringify(detail);
  return String(detail);
}

async function handle(res) {
  let data = {};
  try {
    data = await res.json();
  } catch {
    /* non-JSON body */
  }
  if (!res.ok) {
    const msg = detailToMessage(data.detail) || data.message || `Request failed (${res.status})`;
    const ownership = requestOwners.get(res);
    if (res.status === 409 && msg === 'Interview in progress in another session'
        && ownership?.owner && ownership.owner === interviewLease && ownership.generation === leaseGeneration) {
      ownership.onLost?.();
    }
    const error = new Error(msg);
    error.status = res.status;
    if (data.detail && typeof data.detail === 'object' && !Array.isArray(data.detail)) {
      error.code = data.detail.code;
      error.reason = data.detail.reason;
      error.audioRetained = data.detail.audio_retained;
      error.recoveryAction = data.detail.recovery_action;
    }
    throw error;
  }
  return data;
}

async function post(path, body) {
  const res = await interviewFetch(`${BASE}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  return handle(res);
}

async function get(path) {
  const res = await interviewFetch(`${BASE}${path}`, { method: 'GET' });
  return handle(res);
}

async function put(path, body) {
  const res = await interviewFetch(`${BASE}${path}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  return handle(res);
}

async function del(path) {
  const res = await interviewFetch(`${BASE}${path}`, { method: 'DELETE' });
  return handle(res);
}

export const startInterview = (sessionId, candidateId) => {
  if (interviewLease) managedPresenceSessions.add(`${sessionId}:${Number(candidateId)}`);
  return post('/ai-interview/start', { session_id: sessionId, candidate_id: Number(candidateId) });
};

export const sendTurn = (sessionId, candidateId, message, pasted = false, clientTurnId = null) =>
  post('/ai-interview/turn', {
    session_id: sessionId, candidate_id: Number(candidateId), message, pasted: !!pasted,
    ...(clientTurnId != null ? { client_turn_id: clientTurnId } : {}),
  });

export const submitTaskAnswer = (
  sessionId,
  candidateId,
  taskId,
  answerText,
  clientTaskId = null,
  status = 'submitted',
  timeSpentSeconds = null,
  integrityFlags = {},
) =>
  post('/ai-interview/task-answer', {
    session_id: sessionId,
    candidate_id: Number(candidateId),
    task_id: taskId,
    answer_text: answerText || '',
    status,
    integrity_flags: integrityFlags || {},
    ...(timeSpentSeconds != null ? { time_spent_seconds: Number(timeSpentSeconds) } : {}),
    ...(clientTaskId != null ? { client_task_id: clientTaskId } : {}),
  });

// Presence signal — pauses the server-side silence monitor while the candidate is away (tab hidden /
// refresh / network), and notes a rejoin. Best-effort; the interview works without it.
export const sendPresence = (sessionId, candidateId, present, rejoined = false, invitationId = null) => {
  const key = `${sessionId}:${Number(candidateId)}`;
  if (interviewLease) managedPresenceSessions.add(key);
  if (managedPresenceSessions.has(key) && !interviewLease) return Promise.resolve({ ok: true, ignored: true });
  return post('/ai-interview/presence', {
    session_id: sessionId, candidate_id: Number(candidateId), present: !!present, rejoined: !!rejoined,
    ...(interviewLease ? { presence_sequence: nextPresenceSequence(interviewLease) } : {}),
    ...(invitationId ? { final_qa_invitation_id: invitationId } : {}),
  });
};

// Fire-and-forget "going away" signal for page hide/refresh/close (a normal fetch is cancelled on unload;
// a beacon survives). Pauses the server silence monitor so a reconnect window isn't penalised.
export const presenceBeacon = (sessionId, candidateId, present, { unloading = false } = {}) => {
  try {
    const managed = managedPresenceSessions.has(`${sessionId}:${Number(candidateId)}`);
    if (managed && (unloading || !interviewLease)) return false;
    if (typeof navigator === 'undefined' || !navigator.sendBeacon) return false;
    const body = new Blob(
      [JSON.stringify({ session_id: sessionId, candidate_id: Number(candidateId), present: !!present,
        rejoined: false, ...(interviewLease ? { interview_lease: interviewLease,
          scheduled_interview_id: scheduledInterviewId,
          presence_sequence: nextPresenceSequence(interviewLease) } : {}) })],
      { type: 'application/json' },
    );
    return navigator.sendBeacon(`${BASE}/ai-interview/presence`, body);
  } catch {
    return false;
  }
};

export const getStatus = (sessionId, candidateId) =>
  get(`/ai-interview/status?session_id=${encodeURIComponent(sessionId)}&candidate_id=${Number(candidateId)}`);

export const getAiLaunchStatus = (token) =>
  get(`/interviews/ai-launch/${encodeURIComponent(token)}`);

export const claimAiLaunch = (token, owner, previousOwner = null) =>
  post(`/interviews/ai-launch/${encodeURIComponent(token)}/claim`,
    previousOwner ? { owner, previous_owner: previousOwner } : { owner });
export const renewAiLaunch = (token, owner) =>
  post(`/interviews/ai-launch/${encodeURIComponent(token)}/renew`, { owner });
export const releaseAiLaunch = async (token, owner, { keepalive = false, markAway = false } = {}) => {
  const response = await fetch(`${BASE}/interviews/ai-launch/${encodeURIComponent(token)}/release`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ owner, ...(markAway ? {
      mark_away: true, presence_sequence: nextPresenceSequence(owner),
    } : {}) }), keepalive,
  });
  return handle(response);
};

export const getTranscript = (sessionId, candidateId) =>
  get(`/ai-interview/transcript?session_id=${encodeURIComponent(sessionId)}&candidate_id=${Number(candidateId)}`);

export const getResult = (sessionId, candidateId, interviewId, refresh = false, recalculationBasis) =>
  get(
    `/ai-interview/result?session_id=${encodeURIComponent(sessionId)}&candidate_id=${Number(candidateId)}` +
      (interviewId ? `&interview_id=${encodeURIComponent(interviewId)}` : '') +
      (refresh ? '&refresh=true' : '') +
      (refresh && recalculationBasis ? `&recalculation_basis=${encodeURIComponent(recalculationBasis)}` : ''),
  );

export const getHistory = (sessionId, candidateId) =>
  get(`/ai-interview/history?session_id=${encodeURIComponent(sessionId)}&candidate_id=${Number(candidateId)}`);

export const endInterview = (sessionId, candidateId, reason = 'aborted') =>
  post('/ai-interview/end', { session_id: sessionId, candidate_id: Number(candidateId), reason });

// Full prior turns + current pointer — restores the whole conversation in the UI on rejoin.
export const getHistoryDetail = (sessionId, candidateId) =>
  get(`/ai-interview/history-detail?session_id=${encodeURIComponent(sessionId)}&candidate_id=${Number(candidateId)}`);

// ── interview content configuration (recruiter "Interview Builder") ──────────
const cfgQs = (companyId, sessionId = '') =>
  `?company_id=${encodeURIComponent(companyId || '')}${sessionId ? `&session_id=${encodeURIComponent(sessionId)}` : ''}`;

export const listInterviewConfigurations = (companyId) =>
  get(`/ai-interview/config/list${cfgQs(companyId)}`);
export const getInterviewConfiguration = (companyId, sessionId = '', raw = false) =>
  get(`/ai-interview/config${cfgQs(companyId, sessionId)}${raw ? '&raw=true' : ''}`);
export const getInterviewConfigurationById = (id) =>
  get(`/ai-interview/config/${id}`);
export const createInterviewConfiguration = (payload, companyId) =>
  post(`/ai-interview/config${cfgQs(companyId)}`, payload);
export const updateInterviewConfiguration = (id, payload) =>
  put(`/ai-interview/config/${id}`, payload);
export const deleteInterviewConfiguration = (id) =>
  del(`/ai-interview/config/${id}`);
// config.py base defaults + enriched category registry — the builder seeds from this (never hardcodes).
export const getInterviewDefaults = (companyId) =>
  get(`/ai-interview/config/defaults${cfgQs(companyId)}`);
// Company profile (name/address/description on the company row) the AI uses to answer candidate questions.
export const getCompanyProfile = (companyId) =>
  get(`/ai-interview/config/company-profile${cfgQs(companyId)}`);
export const saveCompanyProfile = (companyId, payload) =>
  post(`/ai-interview/config/company-profile${cfgQs(companyId)}`, payload);
export const getCompanyPolicyStatus = (companyId) =>
  get(`/ai-interview/config/company-policy/status${cfgQs(companyId)}`);
export const listCompanyPolicyDocuments = (companyId) =>
  get(`/ai-interview/config/company-policy/documents${cfgQs(companyId)}`);
export const addCompanyPolicyDocument = (companyId, payload) =>
  post(`/ai-interview/config/company-policy/documents${cfgQs(companyId)}`, payload);
export const deleteCompanyPolicyDocument = (companyId, documentId) =>
  del(`/ai-interview/config/company-policy/documents/${encodeURIComponent(documentId)}${cfgQs(companyId)}`);
export const getCategoryRegistry = (companyId) =>
  get(`/ai-interview/config/categories${cfgQs(companyId)}`);
export const createCategory = (companyId, name, description = '') =>
  post(`/ai-interview/config/categories${cfgQs(companyId)}`, { name, description });
export const deleteCategory = (id) =>
  del(`/ai-interview/config/categories/${id}`);
export const previewBlueprint = (payload, companyId) =>
  post(`/ai-interview/config/preview${cfgQs(companyId)}`, payload);

// Validate recruiter priority topics against the job (title + JD, resolved from session_id when only
// that is given). Returns { relevant[], irrelevant[], advisory: { questions, minutes, breakdown } }.
export const validateTopics = (companyId, { topics = [], sessionId = '', jobTitle = '', jobDescription = '' } = {}) =>
  post(`/ai-interview/config/validate-topics${cfgQs(companyId)}`, {
    topics, session_id: sessionId, job_title: jobTitle, job_description: jobDescription,
  });

export const interviewWsUrl = (sessionId) => {
  const base =
    SOCKET_URL ||
    (typeof window !== 'undefined'
      ? `${window.location.origin.replace(/^http/, 'ws')}/ws`
      : 'ws://localhost:8100/ws');
  return `${base}/ai-interview/${encodeURIComponent(sessionId)}`;
};

// ── voice / media ───────────────────────────────────────────────────
export const createMediaSession = (sessionId, candidateId, mode = 'voice') =>
  post('/ai-interview/media/session', {
    session_id: sessionId,
    candidate_id: Number(candidateId),
    mode,
  });

export const getMediaCapabilities = () => get('/ai-interview/media/capabilities');

// Server-TTS WAV of an AI line, so the browser can mix Wiza's voice INTO the proctoring recording
// (SpeechSynthesis output can't be captured). Returns an ArrayBuffer, or null if TTS is unavailable
// (e.g. no Piper voice configured) → the caller silently skips and the recording just lacks AI audio.
export const fetchAiTts = async (text) => {
  try {
  const res = await interviewFetch(`${BASE}/ai-interview/media/tts?text=${encodeURIComponent((text || '').slice(0, 2000))}`);
    if (!res.ok) return null;
    return await res.arrayBuffer();
  } catch {
    return null;
  }
};

// Hybrid voice turn: POST one endpointed utterance as raw 16-bit PCM mono. The server runs Whisper
// for accuracy and returns the standard TurnResponse; browser captions are provisional.
export const transcribeVoiceTurn = async (
  sessionId, candidateId, pcmBuffer, fallback = '', lang = 'en', lowQuality = false, clientTurnId = null,
  captionSource = 'unknown', diagnostics = {},
) => {
  const captionChars = Array.from(fallback || '');
  let captionLimit = Math.min(captionChars.length, 4000);
  let encodedCaption = encodeURIComponent(captionChars.slice(0, captionLimit).join(''));
  while (encodedCaption.length > 4000) {
    captionLimit = Math.floor(captionLimit * 0.8);
    encodedCaption = encodeURIComponent(captionChars.slice(0, captionLimit).join(''));
  }
  const qs = new URLSearchParams({
    session_id: sessionId,
    candidate_id: String(Number(candidateId)),
    lang,
    low_quality: lowQuality ? 'true' : 'false',
    ...(clientTurnId != null ? { client_turn_id: String(clientTurnId) } : {}),
  }).toString();
  const res = await interviewFetch(`${BASE}/ai-interview/media/transcribe?${qs}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/octet-stream',
      'X-Browser-Caption': encodedCaption,
      'X-Caption-Source': captionSource,
      'X-Capture-Metrics': JSON.stringify(diagnostics),
    },
    body: pcmBuffer,
  });
  return handle(res);   // throws on 422 no_speech → caller keeps listening
};

export const uploadVoiceChunk = async (sessionId, candidateId, utteranceId, seq, pcm) => {
  const qs = new URLSearchParams({ session_id: sessionId, candidate_id: String(candidateId),
    utterance_id: utteranceId, seq: String(seq) }).toString();
  const res = await interviewFetch(`${BASE}/ai-interview/media/turn-audio/chunk?${qs}`, {
    method: 'POST', headers: { 'Content-Type': 'application/octet-stream' }, body: pcm,
  });
  return handle(res);
};

export const getVoiceUploadStatus = (sessionId, candidateId, utteranceId) => {
  const qs = new URLSearchParams({ session_id: sessionId, candidate_id: String(candidateId),
    utterance_id: utteranceId }).toString();
  return get(`/ai-interview/media/turn-audio/status?${qs}`);
};

export const finalizeVoiceUpload = (sessionId, candidateId, utteranceId, details) =>
  post('/ai-interview/media/turn-audio/finalize', {
    session_id: sessionId, candidate_id: Number(candidateId), utterance_id: utteranceId, ...details,
  });

export const checkInterviewAudio = async (pcmBuffer, browserCaption = '', captionSource = 'unknown', diagnostics = {}, lowQuality = false) => {
  const captionChars = Array.from(browserCaption || '');
  let captionLimit = Math.min(captionChars.length, 4000);
  let encodedCaption = encodeURIComponent(captionChars.slice(0, captionLimit).join(''));
  while (encodedCaption.length > 4000) {
    captionLimit = Math.floor(captionLimit * 0.8);
    encodedCaption = encodeURIComponent(captionChars.slice(0, captionLimit).join(''));
  }
  const res = await interviewFetch(`${BASE}/ai-interview/media/audio-check?low_quality=${lowQuality ? 'true' : 'false'}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/octet-stream',
      'X-Browser-Caption': encodedCaption,
      'X-Caption-Source': captionSource,
      'X-Capture-Metrics': JSON.stringify(diagnostics),
      'X-Audio-Check-Id': `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`,
    },
    body: pcmBuffer,
  });
  return handle(res);
};

// ── Job Seeker Mock Interview ────────────────────────────────────────
// Same engine, keyed by (uid + job_listing_id) instead of (session_id + candidate_id). All endpoints
// are PUBLIC under /ai-interview/mock, so the same plain-fetch helpers apply (no auth / no crypto).
const mockQs = (uid, jobListingId, extra = {}) =>
  new URLSearchParams({
    uid: String(Number(uid)),
    job_listing_id: String(jobListingId || ''),
    ...extra,
  }).toString();

export const startMockInterview = (uid, jobListingId, targetQuestions = null, durationMinutes = null) =>
  post('/ai-interview/mock/start', {
    uid: Number(uid), job_listing_id: jobListingId,
    target_questions: targetQuestions, duration_minutes: durationMinutes,
  });

export const sendMockTurn = (uid, jobListingId, message, pasted = false) =>
  post('/ai-interview/mock/turn', {
    uid: Number(uid), job_listing_id: jobListingId, message, pasted: !!pasted,
  });

export const sendMockPresence = (uid, jobListingId, present, rejoined = false, invitationId = null) =>
  post('/ai-interview/mock/presence', {
    uid: Number(uid), job_listing_id: jobListingId, present: !!present, rejoined: !!rejoined,
    ...(invitationId ? { final_qa_invitation_id: invitationId } : {}),
  });

export const mockPresenceBeacon = (uid, jobListingId, present) => {
  try {
    if (typeof navigator === 'undefined' || !navigator.sendBeacon) return false;
    const body = new Blob(
      [JSON.stringify({ uid: Number(uid), job_listing_id: jobListingId, present: !!present, rejoined: false })],
      { type: 'application/json' },
    );
    return navigator.sendBeacon(`${BASE}/ai-interview/mock/presence`, body);
  } catch {
    return false;
  }
};

export const getMockStatus = (uid, jobListingId) =>
  get(`/ai-interview/mock/status?${mockQs(uid, jobListingId)}`);

export const getMockTranscript = (uid, jobListingId) =>
  get(`/ai-interview/mock/transcript?${mockQs(uid, jobListingId)}`);

export const getMockResult = (uid, jobListingId, interviewId, refresh = false, recalculationBasis) =>
  get(`/ai-interview/mock/result?${mockQs(uid, jobListingId, {
    ...(interviewId ? { interview_id: interviewId } : {}),
    ...(refresh ? { refresh: 'true' } : {}),
    ...(refresh && recalculationBasis ? { recalculation_basis: recalculationBasis } : {}),
  })}`);

export const getMockAttempts = (uid, jobListingId) =>
  get(`/ai-interview/mock/attempts?${mockQs(uid, jobListingId)}`);

export const getMockHistory = (uid, jobListingId) =>
  get(`/ai-interview/mock/history?${mockQs(uid, jobListingId)}`);

export const getMockHistoryDetail = (uid, jobListingId) =>
  get(`/ai-interview/mock/history-detail?${mockQs(uid, jobListingId)}`);

export const endMockInterview = (uid, jobListingId, reason = 'ended_by_candidate') =>
  post('/ai-interview/mock/end', { uid: Number(uid), job_listing_id: jobListingId, reason });

// Mock voice turn: POST one endpointed utterance as raw 16-bit PCM mono → standard TurnResponse.
export const transcribeMockVoiceTurn = async (uid, jobListingId, pcmBuffer, fallback = '', lang = 'en') => {
  const res = await interviewFetch(
    `${BASE}/ai-interview/mock/transcribe?${mockQs(uid, jobListingId, { lang, fallback: fallback || '' })}`,
    { method: 'POST', headers: { 'Content-Type': 'application/octet-stream' }, body: pcmBuffer },
  );
  return handle(res);   // throws on 422 no_speech → caller keeps listening
};

// Global mock interview config (HR Super-Admin page).
export const getMockConfig = () => get('/ai-interview/mock/config');
export const getMockConfigDefaults = () => get('/ai-interview/mock/config/defaults');
export const saveMockConfig = (payload) => post('/ai-interview/mock/config', payload);

// ── proctoring / onboarding ─────────────────────────────────────────
const procQs = (companyId, sessionId, candidateId, extra = {}) =>
  new URLSearchParams({
    company_id: String(companyId || ''),
    session_id: String(sessionId || ''),
    candidate_id: String(candidateId || ''),
    ...extra,
  }).toString();

export const getInterviewConfig = (companyId, sessionId, candidateId) =>
  get(`/ai-interview/proctoring/config?${procQs(companyId, sessionId, candidateId)}`);

export const saveConfigDefaults = (patch, companyId = '', sessionId = '') =>
  post(`/ai-interview/proctoring/config/defaults?${procQs(companyId, sessionId, '')}`, { patch });

export const getVoiceParagraph = (index) =>
  get(`/ai-interview/proctoring/verify/voice/paragraph${index != null ? `?index=${index}` : ''}`);

export const verifyPhoto = async (companyId, sessionId, candidateId, blob) => {
  const res = await interviewFetch(
    `${BASE}/ai-interview/proctoring/verify/photo?${procQs(companyId, sessionId, candidateId)}`,
    { method: 'POST', headers: { 'Content-Type': 'application/octet-stream' }, body: blob },
  );
  return handle(res);
};

export const verifyVoice = async (
  companyId, sessionId, candidateId, pcmBuffer, paragraphIndex = -1, lang = 'en',
  browserCaption = '', captionSource = 'unknown', captureMetrics = {},
) => {
  const res = await interviewFetch(
    `${BASE}/ai-interview/proctoring/verify/voice?${procQs(companyId, sessionId, candidateId, {
      paragraph_index: String(paragraphIndex), lang,
    })}`,
    { method: 'POST', headers: {
      'Content-Type': 'application/octet-stream',
      'X-Browser-Caption': encodeURIComponent(browserCaption.slice(0, 4000)),
      'X-Caption-Source': captionSource,
      'X-Capture-Metrics': JSON.stringify(captureMetrics),
    }, body: pcmBuffer },
  );
  return handle(res);
};

export const confirmVoice = (companyId, sessionId, candidateId, attemptId) =>
  post(`/ai-interview/proctoring/verify/voice/confirm?${procQs(companyId, sessionId, candidateId)}`,
    { attempt_id: attemptId });

export const verifyRejoinPhoto = async (companyId, sessionId, candidateId, blob) => {
  const res = await interviewFetch(
    `${BASE}/ai-interview/proctoring/verify/rejoin-photo?${procQs(companyId, sessionId, candidateId)}`,
    { method: 'POST', headers: { 'Content-Type': 'application/octet-stream' }, body: blob },
  );
  return handle(res);
};

export const verifyNetwork = (companyId, sessionId, candidateId, result) =>
  post(`/ai-interview/proctoring/verify/network?${procQs(companyId, sessionId, candidateId)}`, result);

export const proctoringPingUrl = () => `${BASE}/ai-interview/proctoring/ping`;

// ── proctoring recordings (entire screen / camera / mic, chunked) ───
export const uploadRecordingChunk = async (
  companyId, sessionId, candidateId, kind, recordingId, seq, blob,
) => {
  const qs = procQs(companyId, sessionId, candidateId, {
    kind, recording_id: recordingId, seq: String(seq),
  });
  const res = await interviewFetch(`${BASE}/ai-interview/proctoring/recording/chunk?${qs}`, {
    method: 'POST', headers: { 'Content-Type': 'application/octet-stream' }, body: blob,
  });
  return handle(res);
};

export const stopRecording = (companyId, sessionId, candidateId, kind, recordingId, meta = {}) =>
  post(
    `/ai-interview/proctoring/recording/stop?${procQs(companyId, sessionId, candidateId, {
      kind, recording_id: recordingId,
    })}`,
    meta,
  );

// Recordings index for an interview run ({screen,camera,audio} → [{path, server_started_at, ended_at}]),
// for aligning the video to the transcript timeline (future: split the interview into UI sections).
export const getRecordings = (companyId, sessionId, candidateId, interviewId) =>
  get(`/ai-interview/proctoring/recordings?${procQs(companyId, sessionId, candidateId, interviewId ? { interview_id: interviewId } : {})}`);

// ── report playback (recruiter side): per-kind status, lazy stream URL, event timeline, verification ──
export const getRecordingStatus = (companyId, sessionId, candidateId, kind, interviewId) =>
  get(`/ai-interview/proctoring/recording/${kind}/status?${procQs(companyId, sessionId, candidateId, interviewId ? { interview_id: interviewId } : {})}`);

// Retry a failed/stuck conversion (re-encodes from the preserved source .webm). Returns the fresh status.
export const retryRecordingFinalize = (companyId, sessionId, candidateId, kind, interviewId) =>
  post(`/ai-interview/proctoring/recording/${kind}/finalize?${procQs(companyId, sessionId, candidateId, interviewId ? { interview_id: interviewId } : {})}`, {});

// Full URL for a <video src> — bytes stream lazily with HTTP Range (nothing loads until play/seek).
export const recordingStreamUrl = (companyId, sessionId, candidateId, kind, recordingId) =>
  `${BASE}/ai-interview/proctoring/recording/${kind}/stream?${procQs(companyId, sessionId, candidateId, { recording_id: recordingId })}`;

export const getProctoringEvents = (companyId, sessionId, candidateId, interviewId) =>
  get(`/ai-interview/proctoring/proctoring-events?${procQs(companyId, sessionId, candidateId, interviewId ? { interview_id: interviewId } : {})}`);

export const getVerificationList = (companyId, sessionId, candidateId) =>
  get(`/ai-interview/proctoring/verification/list?${procQs(companyId, sessionId, candidateId)}`);

export const verificationFileUrl = (companyId, sessionId, candidateId, category, name) =>
  `${BASE}/ai-interview/proctoring/verification/file?${procQs(companyId, sessionId, candidateId, { category, name })}`;

// Downloads (direct links): the report as a PDF and the whole interview as a ZIP.
export const reportPdfUrl = (companyId, sessionId, candidateId, interviewId, detail = 'summary') =>
  `${BASE}/ai-interview/proctoring/report/pdf?${procQs(companyId, sessionId, candidateId, { ...(interviewId ? { interview_id: interviewId } : {}), detail })}`;

export const exportZipUrl = (companyId, sessionId, candidateId, interviewId) =>
  `${BASE}/ai-interview/proctoring/export.zip?${procQs(companyId, sessionId, candidateId, interviewId ? { interview_id: interviewId } : {})}`;

// Fire-and-forget finalize for page refresh/close (fetch gets cancelled on unload; beacons don't).
// The backend remuxes whatever chunks are on disk, so the recording stays viewable "till the issue".
export const recordingStopBeacon = (companyId, sessionId, candidateId, kind, recordingId) => {
  try {
    if (typeof navigator === 'undefined' || !navigator.sendBeacon) return false;
    const url = `${BASE}/ai-interview/proctoring/recording/stop?${procQs(companyId, sessionId, candidateId, {
      kind, recording_id: recordingId,
    })}`;
    return navigator.sendBeacon(url, new Blob([JSON.stringify(interviewLease
      ? { interview_lease: interviewLease, scheduled_interview_id: scheduledInterviewId } : {})], { type: 'application/json' }));
  } catch {
    return false;
  }
};

export const mediaWsUrl = (sessionId) => {
  const base =
    SOCKET_URL ||
    (typeof window !== 'undefined'
      ? `${window.location.origin.replace(/^http/, 'ws')}/ws`
      : 'ws://localhost:8100/ws');
  return `${base}/ai-interview/media/${encodeURIComponent(sessionId)}`;
};
